import { Injectable } from '@angular/core';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { of } from 'rxjs';
import { catchError, delay, map, mergeMap } from 'rxjs/operators';
import {
  MixpanelEventType,
  MixpanelService,
} from 'src/app/core/services/mixpanel-service/mixpanel.service';
import { Messages } from 'src/app/core/services/notification-service/notification-messages';
import { NotificationService } from 'src/app/core/services/notification-service/notification.service';
import { TenantService } from 'src/app/features/settings/services/tenant-service/tenant.service';
import { UsersService } from 'src/app/features/settings/services/users-service/users.service';
import * as UserManagementActions from './users.actions';
import { UserGroupsActions } from '../user-groups/user-groups.actions';

@Injectable()
export class UserManagementStateEffects {
  loadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementActions.LoadUsersAction),
      mergeMap(() =>
        this.tenantService.getUsersInCurrentTenant().pipe(
          map((users) =>
            // Pull off and fire an action to indicate we loaded the users
            UserManagementActions.StoreUsersAction({
              users,
            }),
          ),
          catchError((_err) => {
            // Notify there was an error
            this.notificationService.error(Messages.PROBLEM_LOADING_USER_DATA);
            return of(
              UserManagementActions.LoadUsersErrorAction({
                error: Messages.PROBLEM_LOADING_USER_DATA,
              }),
            );
          }),
        ),
      ),
    ),
  );

  updateUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementActions.UpdateUserAction),
      mergeMap((action) =>
        this.usersService.updateUser(action.user.id, action.user).pipe(
          map((updatedUser) => {
            // Mixpanel event
            this.mixpanelService.track(
              MixpanelEventType.USER_MANAGEMENT_EDIT_USER,
              { userId: updatedUser.id },
            );
            // Notify the success
            this.notificationService.info(Messages.USER_UPDATED);
            // Store the user groups
            return UserManagementActions.UpdateUserSuccessAction({
              user: updatedUser,
            });
          }),
          catchError(() => {
            // Notify there was an error
            this.notificationService.error(Messages.PROBLEM_UPDATING_USER_DATA);
            return of(
              UserManagementActions.UserSaveErrorAction({
                error: Messages.PROBLEM_UPDATING_USER_DATA,
              }),
            );
          }),
        ),
      ),
    ),
  );

  addUser$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementActions.AddUserAction),
      mergeMap((action) =>
        this.usersService
          .addUser(action.user, Boolean(action.delayInvite))
          .pipe(
            map((addedUser) => {
              // Mixpanel event
              this.mixpanelService.track(
                !!action.delayInvite
                  ? MixpanelEventType.FTW_ADD_USER
                  : MixpanelEventType.USER_MANAGEMENT_ADD_USER,
                { userId: addedUser.id },
              );
              // Notify the success
              this.notificationService.info(Messages.USER_ADDED);
              // Store the user
              return UserManagementActions.AddUserSuccessAction({
                user: addedUser,
              });
            }),
            catchError(() => {
              // Notify there was an error
              this.notificationService.error(Messages.PROBLEM_ADDING_USER);
              return of(
                UserManagementActions.UserSaveErrorAction({
                  error: Messages.PROBLEM_ADDING_USER,
                }),
              );
            }),
          ),
      ),
    ),
  );

  resendInvite$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementActions.ResendUserInviteAction),
      mergeMap((action) =>
        this.usersService
          .resendUserInvite(action.user.id, Boolean(action.delayInvite))
          .pipe(
            map((user) => {
              // Mixpanel event
              this.mixpanelService.track(
                action.delayInvite
                  ? MixpanelEventType.FTW_USER_REACTIVATED
                  : MixpanelEventType.USER_MANAGEMENT_USER_INVITE_RESENT,
                { userId: user.id },
              );
              // Notify the success
              this.notificationService.info(
                action.delayInvite
                  ? Messages.USER_REACTIVATED
                  : Messages.INVITE_RESENT,
              );
              // Store the user
              return UserManagementActions.ResendUserInviteSuccessAction({
                user,
              });
            }),
            catchError(() => {
              // Notify there was an error
              this.notificationService.error(Messages.PROBLEM_SENDING_INVITE);
              return of(
                UserManagementActions.ResendUserInviteErrorAction({
                  error: Messages.PROBLEM_SENDING_INVITE,
                }),
              );
            }),
          ),
      ),
    ),
  );

  getUpdatedStatuses$ = createEffect(() =>
    this.actions$.pipe(
      ofType(
        UserManagementActions.ResendUserInviteSuccessAction,
        UserManagementActions.AddUserSuccessAction,
      ),
      map(() => UserManagementActions.LoadUsersAction()),
      delay(5000),
    ),
  );

  deactivate$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementActions.DeactivateUserAction),
      mergeMap((action) =>
        this.usersService.deactivateUser(action.user.id).pipe(
          map((user) => {
            // Mixpanel event
            this.mixpanelService.track(
              MixpanelEventType.USER_MANAGEMENT_DEACTIVATE,
              { userId: user.id },
            );
            // Notify the success
            this.notificationService.info(Messages.USER_DEACTIVATED);
            // Store the user
            return UserManagementActions.DeactivateUserSuccessAction({
              user,
            });
          }),
          catchError(() => {
            // Notify there was an error
            this.notificationService.error(Messages.PROBLEM_DEACTIVATING_USER);
            return of(
              UserManagementActions.DeactivateUserErrorAction({
                error: Messages.PROBLEM_DEACTIVATING_USER,
              }),
            );
          }),
        ),
      ),
    ),
  );

  LoadUsers$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementActions.UserManagementLoaded),
      map(() => UserManagementActions.LoadUsersAction()),
    ),
  );

  LoadUserGroups$ = createEffect(() =>
    this.actions$.pipe(
      ofType(UserManagementActions.UserManagementLoaded),
      map(() => UserGroupsActions.loadUserGroups()),
    ),
  );

  constructor(
    private actions$: Actions,
    private tenantService: TenantService,
    private usersService: UsersService,
    private notificationService: NotificationService,
    private mixpanelService: MixpanelService,
  ) {}
}
